import { NEUCLEOS_SIGN_IN_ENDED_MESSAGE } from "@cleo/contracts";
import { create } from "zustand";

import { appHistoryKindForShell, hashRoutePath, hashRouteSearch } from "../appHistory";
import { isElectron } from "../env";
import { fetchPrimary } from "../environments/primary/fetch";
import { worldViewFrameKind } from "../worldViewRoute";
import { isWebHost } from "../webHost";
import {
  ALL_WORLDS_DOOR,
  LOCAL_WORLD_DOOR,
  allModeActive,
  localModeActive,
  resolveWorldSelection,
  worldSelectionHref,
  type NeucleosWorldSelection,
  type NeucleosWorldSummary,
  type WorldThreadScope,
} from "./worldSelection";

const ROOM_STORAGE_KEY = "neucleos.worldRoom";
const LAST_DOOR_STORAGE_KEY = "neucleos.lastDoor";

/** The door (World key or the Local sentinel) the person last stood in. */
export function readLastDoor(): string {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(LAST_DOOR_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

const LAST_STANCE_STORAGE_KEY = "cleo:neucleos:last-stance";

/** RM-CAN-14: the last CHOSEN stance ("local" or none). Not World memory (F03/W42 stands). */
export function readLastStance(): string {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(LAST_STANCE_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

export function rememberLastStance(doorKey: string): void {
  if (typeof window === "undefined") return;
  try {
    if (doorKey === LOCAL_WORLD_DOOR) window.localStorage.setItem(LAST_STANCE_STORAGE_KEY, doorKey);
    else window.localStorage.removeItem(LAST_STANCE_STORAGE_KEY);
  } catch {
    // Stance memory is a preference, not the World.
  }
}

export function rememberLastDoor(doorKey: string): void {
  if (typeof window === "undefined") return;
  if (doorKey.length === 0) return;
  try {
    window.localStorage.setItem(LAST_DOOR_STORAGE_KEY, doorKey);
  } catch {
    // Door memory is the prefs analogue, not the World.
  }
}

export interface NeucleosWorldStatus extends NeucleosWorldSummary {
  readonly actorId?: string;
  readonly mcpTokenPresent: boolean;
  readonly expiresAt: number;
  /** Which grants the Desk holds for the World (server tokenStore.ts publicSessionStatus); a renewal keeps it. */
  readonly grantKey?: string;
}

/** W-2a2288b5: a World whose access a sign-out could not revoke yet. The
 * Desk keeps it and retries until the World confirms or it expires. */
export interface NeucleosPendingRevocation {
  readonly worldKey: string;
  readonly name: string;
  readonly expiresAt: number;
  /** When the Desk next asks the World again (bounded backoff). */
  readonly nextAttemptAt?: number;
}

interface NeucleosWorldState {
  readonly identity: string | null;
  readonly signedIn: boolean;
  readonly worlds: ReadonlyArray<NeucleosWorldStatus>;
  readonly pendingRevocations: ReadonlyArray<NeucleosPendingRevocation>;
  /** W-2308dc50: the fixed day this Mac's sign-in ends, once it is a few
   * days away (the Desk renews it until then); null otherwise. */
  readonly signInEndsAt: number | null;
  /** W-610f1054: Worlds the World lists for this person that this Mac holds no access for yet, by key. */
  readonly worldsNotHeld: ReadonlyArray<string>;
  readonly selection: NeucleosWorldSelection | null;
  readonly activeWorldKey: string | null;
  /** Changes only when the public grant snapshot changes. Room reads depend
   * on this so a fresh grant for the SAME World invalidates a prior refusal. */
  readonly grantRevision: number;
  readonly loaded: boolean;
  hydrate: (input: {
    readonly identity: string | null;
    readonly signedIn: boolean;
    readonly worlds: ReadonlyArray<NeucleosWorldStatus>;
    readonly pendingRevocations?: ReadonlyArray<NeucleosPendingRevocation>;
    readonly signInEndsAt?: number | null;
    readonly worldsNotHeld?: ReadonlyArray<string>;
    readonly requestedWorldKey?: string;
  }) => void;
}

function readRequestedWorldKey(): string {
  if (typeof window === "undefined") return "";
  const hashSearch = hashRouteSearch(window.location.hash);
  return new URLSearchParams(hashSearch ?? window.location.search).get("world")?.trim() ?? "";
}

/** The World's name when the link carries one (`&worldName=`), read like the World key. */
function readRequestedWorldName(): string {
  if (typeof window === "undefined") return "";
  const hashSearch = hashRouteSearch(window.location.hash);
  return new URLSearchParams(hashSearch ?? window.location.search).get("worldName")?.trim() ?? "";
}

/** The part of the app router the World URL repair uses. */
export interface WorldUrlRouter {
  readonly latestLocation: {
    readonly pathname: string;
    readonly search: Readonly<Record<string, unknown>>;
  };
  navigate(options: never): Promise<void>;
}

let worldUrlRouter: WorldUrlRouter | null = null;

/** main.tsx binds the app router at boot; from then on the repair goes through it. */
export function bindWorldUrlRouter(router: WorldUrlRouter): () => void {
  worldUrlRouter = router;
  return () => {
    if (worldUrlRouter === router) worldUrlRouter = null;
  };
}

export function keepWorldInUrl(worldKey: string): void {
  if (typeof window === "undefined") return;
  const router = worldUrlRouter;
  if (router !== null) {
    // W-c83c3467: the router moves its own location at once and writes the
    // window URL a microtask later, and it treats an outside replaceState as a
    // navigation. Reading window.location here repaired the page the router had
    // just left and sent the router back to it, so the first Usage click after
    // Cmd+K was lost. Compare the router's location and repair through it.
    const location = router.latestLocation;
    const current = location.search["world"];
    if (current !== undefined && String(current) === worldKey) return;
    void router.navigate({
      to: location.pathname,
      search: { ...location.search, world: worldKey },
      hash: true,
      state: true,
      replace: true,
      resetScroll: false,
    } as never);
    return;
  }
  // No router is bound (tests of this module alone): nothing to race.
  const routePath = hashRoutePath(window.location.hash);
  const params = new URLSearchParams(
    routePath === null ? window.location.search : (hashRouteSearch(window.location.hash) ?? ""),
  );
  if (params.get("world") === worldKey) return;
  params.set("world", worldKey);
  const query = params.toString();
  const nextRoute = `${routePath ?? window.location.pathname}${query ? `?${query}` : ""}`;
  const nextUrl =
    routePath === null
      ? nextRoute
      : `${window.location.pathname}${window.location.search}#${nextRoute}`;
  // URL repair must retain the router key/index and any caller history state.
  window.history.replaceState(window.history.state ?? null, "", nextUrl);
}

function readRoomMap(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(ROOM_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return parsed as Record<string, string>;
  } catch {
    return {};
  }
}

/**
 * A World's remembered room is a room: the index room or one addressed
 * session leaf, per the one room classifier. Utility pages (Settings, Usage,
 * Pull Requests…) keep the World's context but never become its room. /pair
 * and /connect are onboarding surfaces outside the classifier's map.
 */
function isRememberableRoom(pathname: string): boolean {
  if (pathname === "/pair" || pathname.startsWith("/connect")) return false;
  return pathname === "/" || worldViewFrameKind(pathname) === "session";
}

export function readWorldRoom(worldKey: string): string {
  const path = readRoomMap()[worldKey];
  if (typeof path !== "string") return "/";
  const clean = path.trim().split("?")[0] ?? "/";
  if (!clean.startsWith("/")) return "/";
  if (!isRememberableRoom(clean)) return "/";
  return clean;
}

export function rememberWorldRoom(worldKey: string, pathname: string): void {
  if (typeof window === "undefined") return;
  if (worldKey.length === 0) return;
  const clean = pathname.trim().split("?")[0] ?? "/";
  if (!clean.startsWith("/")) return;
  if (!isRememberableRoom(clean)) return;
  // The landing path is where restoration STARTS, never a room to keep: a
  // cold boot passes through "/" before the async restore reads this slot,
  // and remembering it would let the boot write race the reader and win
  // (WS9). "/" stays a valid read fallback for an empty slot.
  if (clean === "/") return;
  try {
    const rooms = readRoomMap();
    rooms[worldKey] = clean;
    window.localStorage.setItem(ROOM_STORAGE_KEY, JSON.stringify(rooms));
  } catch {
    // Room memory is the prefs analogue, not the World.
  }
}

/**
 * Forget a World's remembered room (W-b26dffc3): the World refused the session
 * it was filed under, so the slot is cleared and the World opens at its own
 * starting session.
 */
export function forgetWorldRoom(worldKey: string): void {
  if (typeof window === "undefined") return;
  try {
    const rooms = readRoomMap();
    if (!(worldKey in rooms)) return;
    delete rooms[worldKey];
    window.localStorage.setItem(ROOM_STORAGE_KEY, JSON.stringify(rooms));
  } catch {
    // Room memory is the prefs analogue, not the World.
  }
}

/**
 * The room-memory slot the current pathname belongs to (WS9). Nothing is
 * remembered before hydration answers who we are; every Local operating
 * state — signed-out and zero-Worlds included, per the ONE mode derivation —
 * owns the Local slot; a selected World owns its own key. Distinct from the
 * door-URL/lastDoor law (F03/W42), which stays membership-keys-only.
 */
export function roomMemoryDoorKey(state: {
  readonly loaded: boolean;
  readonly signedIn: boolean;
  readonly worlds: ReadonlyArray<unknown>;
  readonly selection: NeucleosWorldSelection | null;
  readonly activeWorldKey: string | null;
}): string | null {
  if (!state.loaded) return null;
  if (
    localModeActive({
      signedIn: state.signedIn,
      worldCount: state.worlds.length,
      selection: state.selection,
    })
  ) {
    return LOCAL_WORLD_DOOR;
  }
  if (allModeActive(state.selection)) return ALL_WORLDS_DOOR;
  return state.activeWorldKey;
}

/**
 * True when entering a World keeps this page (hash routing: the Desk), so
 * module state such as an owed notice survives the entry; false when the
 * entry reloads the page (browser routing).
 */
export function worldEntryKeepsPage(): boolean {
  return appHistoryKindForShell(isElectron) === "hash";
}

export function enterWorld(
  worldKey: string,
  destination?: { readonly sessionId: string; readonly notice?: string },
): void {
  const { worlds } = useNeucleosWorldStore.getState();
  if (!worlds.some((world) => world.worldKey === worldKey)) return;
  const path = destination
    ? `/session/${encodeURIComponent(destination.sessionId)}`
    : readWorldRoom(worldKey);
  const href =
    worldSelectionHref(path, worldKey) +
    (destination?.notice ? `&notice=${encodeURIComponent(destination.notice)}` : "");
  if (appHistoryKindForShell(isElectron) === "hash") {
    useNeucleosWorldStore.setState({
      selection: { status: "selected", worldKey },
      activeWorldKey: worldKey,
    });
    window.location.assign(`#${href}`);
    return;
  }
  window.location.assign(href);
}

/**
 * The explicit Local door. A root navigation like `enterWorld`, carrying the
 * Local sentinel so a reload lands in the same operating mode. It changes no
 * sign-in state: Local is a mode, not a logout.
 */
/**
 * The ALL stance: the whole entitlement, before any World filter. A root
 * navigation like `enterWorld`, carrying the ALL sentinel so a reload lands
 * in the same stance. A World is a filter; ALL applies none.
 */
export function enterAll(): void {
  const href = worldSelectionHref(readWorldRoom(ALL_WORLDS_DOOR), ALL_WORLDS_DOOR);
  if (appHistoryKindForShell(isElectron) === "hash") {
    useNeucleosWorldStore.setState({
      selection: { status: "all", worldKey: null },
      activeWorldKey: null,
    });
    window.location.assign(`#${href}`);
    return;
  }
  window.location.assign(href);
}

export function enterLocal(): void {
  const href = worldSelectionHref(readWorldRoom(LOCAL_WORLD_DOOR), LOCAL_WORLD_DOOR);
  if (appHistoryKindForShell(isElectron) === "hash") {
    useNeucleosWorldStore.setState({
      selection: { status: "local", worldKey: null },
      activeWorldKey: null,
    });
    window.location.assign(`#${href}`);
    return;
  }
  window.location.assign(href);
}

/**
 * The principal's ruling, captured 2026-08-26: inside a World tab, session
 * birth is GOVERNED — a Session + Agent through the joined birth ceremony —
 * never a bare local thread. Ungoverned threads belong to the Local door;
 * that split is the reason Local stands beside the Worlds at all. Every
 * thread-creation surface gates on this one predicate.
 */
export function worldGovernsSessionBirth(state: {
  readonly loaded: boolean;
  readonly selection: NeucleosWorldSelection | null;
}): boolean {
  return state.loaded && state.selection?.status === "selected";
}

/** The World-scope every thread list filters by; Local admits only ungoverned work. */
export function selectWorldThreadScope(state: {
  readonly activeWorldKey: string | null;
  readonly signedIn: boolean;
  readonly worlds: ReadonlyArray<unknown>;
  readonly selection: NeucleosWorldSelection | null;
}): WorldThreadScope {
  return {
    activeWorldKey: state.activeWorldKey,
    local: localModeActive({
      signedIn: state.signedIn,
      worldCount: state.worlds.length,
      selection: state.selection,
    }),
    all: allModeActive(state.selection),
  };
}

export const useNeucleosWorldStore = create<NeucleosWorldState>((set) => ({
  identity: null,
  signedIn: false,
  worlds: [],
  pendingRevocations: [],
  signInEndsAt: null,
  worldsNotHeld: [],
  selection: null,
  activeWorldKey: null,
  grantRevision: 0,
  loaded: false,
  hydrate: (input) => {
    const requested = input.requestedWorldKey ?? readRequestedWorldKey();
    const requestedName = readRequestedWorldName();
    const selection = resolveWorldSelection(
      input.worlds,
      requested,
      readLastDoor(),
      readLastStance(),
      requestedName,
    );
    // A different account may inherit this window's previous World URL.
    // Memberships decide the landing; an inaccessible URL is not an entitlement.
    // Reload at the root before publishing the new World so the previous
    // account's session route can neither render nor enter its room memory.
    // A link that names the World it wants (Open in Cleo after a purchase) keeps
    // its note instead: the person signs in again to be granted that World.
    if (
      input.signedIn &&
      selection.status === "unavailable" &&
      input.worlds.length > 0 &&
      requestedName === ""
    ) {
      const landing = resolveWorldSelection(input.worlds, "", readLastDoor());
      if (landing.status === "selected" && typeof window !== "undefined") {
        const href = worldSelectionHref("/", landing.worldKey);
        const url =
          appHistoryKindForShell(isElectron) === "hash"
            ? `${window.location.pathname}?world=${encodeURIComponent(landing.worldKey)}#${href}`
            : href;
        window.history.replaceState(null, "", url);
        window.location.reload();
        return;
      }
    }
    const activeWorldKey = selection.status === "selected" ? selection.worldKey : null;
    if (activeWorldKey) keepWorldInUrl(activeWorldKey);
    else if (selection.status === "local") keepWorldInUrl(LOCAL_WORLD_DOOR);
    else if (selection.status === "all") keepWorldInUrl(ALL_WORLDS_DOOR);
    set((state) => {
      const previousGrantSnapshot = JSON.stringify({
        identity: state.identity,
        signedIn: state.signedIn,
        worlds: state.worlds.map(({ worldKey, actorId, mcpTokenPresent, expiresAt, grantKey }) => ({
          worldKey,
          actorId,
          mcpTokenPresent,
          // A renewal moves only the expiry, and reloads nothing (7 Oct 2026: 84 renewals after a 14-World sign-in
          // reloaded the World over and over). A server that names no grants keeps the expiry as the signal.
          grant: grantKey ?? expiresAt,
        })),
      });
      const nextGrantSnapshot = JSON.stringify({
        identity: input.identity,
        signedIn: input.signedIn,
        worlds: input.worlds.map(({ worldKey, actorId, mcpTokenPresent, expiresAt, grantKey }) => ({
          worldKey,
          actorId,
          mcpTokenPresent,
          // A renewal moves only the expiry, and reloads nothing (7 Oct 2026: 84 renewals after a 14-World sign-in
          // reloaded the World over and over). A server that names no grants keeps the expiry as the signal.
          grant: grantKey ?? expiresAt,
        })),
      });
      return {
        identity: input.identity,
        signedIn: input.signedIn,
        worlds: input.worlds,
        pendingRevocations: input.pendingRevocations ?? [],
        signInEndsAt: input.signInEndsAt ?? null,
        worldsNotHeld: input.worldsNotHeld ?? [],
        selection,
        activeWorldKey,
        grantRevision:
          previousGrantSnapshot === nextGrantSnapshot
            ? state.grantRevision
            : state.grantRevision + 1,
        loaded: true,
      };
    });
  },
}));

// Status reads can overlap (the frame, the six-hour timer, a server push, a
// sign-in watcher). Each read takes a number when it starts, and its answer is
// applied only if no later-started read has already been applied, so an older
// response can never restore what a newer one cleared (adversary P2e).
let statusReadsStarted = 0;
let statusReadsApplied = 0;

/**
 * cleo-web (webHost.ts): the browser host has no Desk server to read `/api/neucleos/status` from. WebHostWorldSession.tsx
 * hydrates the store from the World's own answer (worlds:listMyWorlds) and keeps the last one here, so every refresh the
 * screens ask for re-applies it. The World holds no Desk grants for a browser: each World reads as held (mcpTokenPresent)
 * for as long as the Clerk session, with one fixed expiry per sign-in so a re-apply never reads as a new grant.
 */
let webHostWorlds: {
  readonly identity: string | null;
  readonly actorId: string;
  readonly sessionId: string;
  readonly worlds: ReadonlyArray<NeucleosWorldSummary>;
  readonly expiresAt: number;
} | null = null;
const WEB_HOST_WORLD_HOLD_MS = 7 * 24 * 60 * 60 * 1000;

export function hydrateWebHostWorlds(
  answer: {
    readonly identity: string | null;
    readonly actorId: string;
    readonly sessionId: string;
    readonly worlds: ReadonlyArray<NeucleosWorldSummary>;
  } | null,
): void {
  webHostWorlds =
    answer === null
      ? null
      : {
          ...answer,
          expiresAt:
            webHostWorlds?.sessionId === answer.sessionId
              ? webHostWorlds.expiresAt
              : Date.now() + WEB_HOST_WORLD_HOLD_MS,
        };
  const held = webHostWorlds;
  useNeucleosWorldStore.getState().hydrate(
    held === null
      ? { identity: null, signedIn: false, worlds: [] }
      : {
          identity: held.identity,
          signedIn: true,
          worlds: held.worlds.map((world) => ({
            worldKey: world.worldKey,
            ...(world.name === undefined ? {} : { name: world.name }),
            actorId: held.actorId,
            mcpTokenPresent: true,
            expiresAt: held.expiresAt,
          })),
        },
  );
}

export async function refreshNeucleosWorlds(): Promise<void> {
  if (isWebHost()) {
    hydrateWebHostWorlds(webHostWorlds);
    return;
  }
  const readNumber = ++statusReadsStarted;
  const response = await fetchPrimary("/api/neucleos/status");
  if (!response.ok) {
    // A failed read is not an answer (W-2308dc50, adversary finding 4): once
    // the store holds the server's answer, a busy or restarting local server
    // changes nothing. Only its own signed-out answer signs the store out.
    // Before any answer, the store is still told it has loaded, as before.
    if (useNeucleosWorldStore.getState().loaded) return;
    useNeucleosWorldStore.getState().hydrate({
      identity: null,
      signedIn: false,
      worlds: [],
    });
    return;
  }
  const body = (await response.json()) as {
    identity?: string | null;
    signedIn?: boolean;
    worlds?: NeucleosWorldStatus[];
    pendingRevocations?: NeucleosPendingRevocation[];
    signInEndsAt?: unknown;
    worldsNotHeld?: unknown;
  };
  if (readNumber < statusReadsApplied) return;
  statusReadsApplied = readNumber;
  useNeucleosWorldStore.getState().hydrate({
    identity: body.identity ?? null,
    signedIn: body.signedIn === true,
    worlds: Array.isArray(body.worlds) ? body.worlds : [],
    pendingRevocations: parsePendingRevocations(body.pendingRevocations),
    signInEndsAt: Number.isSafeInteger(body.signInEndsAt) ? (body.signInEndsAt as number) : null,
    worldsNotHeld: Array.isArray(body.worldsNotHeld)
      ? body.worldsNotHeld.filter((key): key is string => typeof key === "string")
      : [],
  });
}

/**
 * W-4a6095af (Can 31): the sentence for a sign-in that ended without the person
 * signing out: this Desk is not signed in but still names who was. An explicit
 * sign-out (or a Desk never signed in) clears the identity, so it reads null.
 * Null until the Desk has answered once.
 */
export function signInEndedNotice(state: {
  readonly loaded: boolean;
  readonly signedIn: boolean;
  readonly identity: string | null;
}): string | null {
  return state.loaded && !state.signedIn && state.identity !== null
    ? NEUCLEOS_SIGN_IN_ENDED_MESSAGE
    : null;
}

/** W-610f1054: the plain notice while the World lists Worlds this Mac holds no access for yet; null when none. */
export function worldsNotHeldNotice(worldsNotHeld: ReadonlyArray<string>): string | null {
  if (worldsNotHeld.length === 0) return null;
  return worldsNotHeld.length === 1
    ? "You've joined a World that isn't on this Mac yet. Sign in again to bring it here."
    : `You've joined ${worldsNotHeld.length} Worlds that aren't on this Mac yet. Sign in again to bring them here.`;
}

/** W-2308dc50: the plain notice before a sign-in's fixed end; null when none is near. */
export function signInEndingNotice(endsAt: number | null): string | null {
  if (endsAt === null) return null;
  const day = new Date(endsAt).toLocaleDateString(undefined, { dateStyle: "long" });
  return `Your sign-in on this Mac ends on ${day}; sign in again to keep going.`;
}

export function parsePendingRevocations(value: unknown): NeucleosPendingRevocation[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry: unknown) => {
    if (typeof entry !== "object" || entry === null) return [];
    const { worldKey, name, expiresAt, nextAttemptAt } = entry as Record<string, unknown>;
    if (typeof worldKey !== "string" || typeof expiresAt !== "number") return [];
    return [
      {
        worldKey,
        name: typeof name === "string" && name.trim() ? name.trim() : worldKey,
        expiresAt,
        ...(typeof nextAttemptAt === "number" ? { nextAttemptAt } : {}),
      },
    ];
  });
}

/** What the person is told while some World access is still live after
 * sign-out: which Worlds, that the Desk keeps trying, and the latest time
 * the access can last. */
export function pendingRevocationNotice(
  pending: ReadonlyArray<NeucleosPendingRevocation>,
): string | null {
  if (pending.length === 0) return null;
  const names = [...new Set(pending.map((entry) => entry.name))];
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  const latest = new Date(Math.max(...pending.map((entry) => entry.expiresAt))).toLocaleString(
    undefined,
    { dateStyle: "medium", timeStyle: "short" },
  );
  const one = names.length === 1;
  return `Access to ${list} could not be revoked yet: ${one ? "the World" : "those Worlds"} did not confirm. This Mac keeps trying until ${one ? "it does" : "each one does"}; until then that access stays valid, at the latest until ${latest}.`;
}

/** When to read the status door again while access is still live after
 * sign-out: just after the Desk's next retry (or the earliest expiry), never
 * sooner than two seconds from now. null when nothing is pending. */
export function pendingRevocationRecheckDelayMs(
  pending: ReadonlyArray<NeucleosPendingRevocation>,
  now: number,
): number | null {
  if (pending.length === 0) return null;
  const due = Math.min(
    ...pending.map((entry) => Math.min(entry.nextAttemptAt ?? entry.expiresAt, entry.expiresAt)),
  );
  return Math.max(2_000, due - now + 2_000);
}
